import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, X } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useApp } from '@/lib/context';
import { useDetailPanel } from './detail-panel-context';
import { DetailPanel } from './detail-panel';

const UserProfileBottomSheet = lazy(() =>
  import('@/components/user-profile-bottom-sheet').then((m) => ({
    default: m.UserProfileBottomSheet,
  }))
);
const EventDetailSheet = lazy(() => import('@/components/events/event-detail-sheet'));
const GroupDetailSheet = lazy(() =>
  import('@/components/groups/group-detail-sheet').then((m) => ({
    default: m.GroupDetailSheet,
  }))
);
const WallPostViewerModal = lazy(() => import('@/components/wall-post-viewer-modal'));
const ChatConversationView = lazy(() =>
  import('@/components/chat/ChatConversationView').then((m) => ({
    default: m.ChatConversationView,
  }))
);
const NotificationsFeedPanel = lazy(() =>
  import('@/components/notifications/notifications-feed-panel').then((m) => ({
    default: m.NotificationsFeedPanel,
  }))
);

const CRUMB: Record<string, string> = {
  profile: 'Profile',
  event: 'Evento',
  group: 'Gruppo',
  post: 'Post',
  chat: 'Chat',
  notifications: 'Feeds',
};

/**
 * Renders the right-anchored DetailPanel container with the existing
 * sheet's graphics inside (via panelMode). The DetailPanelProvider
 * handles state + ?panel=<kind>/<id> deep-linking; this host just
 * mounts the right canonical sheet for the active kind.
 */
export function DetailPanelHost() {
  const { current, close, open, push } = useDetailPanel();

  // Fetch event by id when an event panel is open.
  const eventId = current?.kind === 'event' ? Number(current.id) : null;
  const { data: eventsData } = useQuery<any>({
    queryKey: ['/api/events'],
    queryFn: () => apiRequest('GET', '/api/events'),
    enabled: eventId !== null && Number.isFinite(eventId),
    staleTime: 60 * 1000,
  });
  const eventForPanel = useMemo(() => {
    if (eventId === null) return null;
    const list = Array.isArray(eventsData) ? eventsData : eventsData?.events || [];
    return list.find((e: any) => e.id === eventId) || null;
  }, [eventsData, eventId]);

  // Find post by id from the wall list when a post panel is open.
  const postId = current?.kind === 'post' ? Number(current.id) : null;
  const { data: postsData } = useQuery<any>({
    queryKey: ['/api/wall'],
    queryFn: () => apiRequest('GET', '/api/wall'),
    enabled: postId !== null && Number.isFinite(postId),
    staleTime: 60 * 1000,
  });
  const postForPanel = useMemo(() => {
    if (postId === null) return null;
    const list = Array.isArray(postsData) ? postsData : postsData?.posts || [];
    return list.find((p: any) => p.id === postId) || null;
  }, [postsData, postId]);

  const { user: currentUser } = useApp();
  const queryClient = useQueryClient();

  // Voto reale dal post aperto nel panel: persiste via API e aggiorna feed + panel.
  const handlePostVote = useCallback(
    async (pid: number, voteType: 'up' | 'down') => {
      try {
        await apiRequest('POST', '/api/wall/vote', { postId: pid, voteType });
        queryClient.invalidateQueries({ queryKey: ['/api/wall'] });
      } catch (err) {
        console.error('[DetailPanelHost] Errore voto post:', err);
      }
    },
    [queryClient]
  );

  if (!current) return null;

  const crumb = `${CRUMB[current.kind] || 'Dettaglio'} · #${current.id}`;

  if (current.kind === 'profile') {
    const userId = Number(current.id);
    if (!Number.isFinite(userId)) return null;
    const isOwnProfile = (currentUser as any)?.id === userId;
    // No breadcrumb for profile panels — the panel chrome stays clean.
    return (
      <DetailPanel
        bareBody
        chromeAction={isOwnProfile ? <ProfileEditChromeAction panelKey={current.id} /> : undefined}
      >
        <Suspense fallback={null}>
          <UserProfileBottomSheet userId={userId} isOpen onClose={close} panelMode />
        </Suspense>
      </DetailPanel>
    );
  }

  if (current.kind === 'event') {
    return (
      <DetailPanel crumb={crumb} bareBody>
        {eventForPanel && (
          <Suspense fallback={null}>
            <EventDetailSheet
              event={eventForPanel}
              isOpen
              onClose={close}
              cardRef={null}
              backgroundStyle={{}}
              macroCategories={[]}
              mockParticipants={[]}
              panelMode
            />
          </Suspense>
        )}
      </DetailPanel>
    );
  }

  if (current.kind === 'post') {
    return (
      <DetailPanel crumb={crumb} bareBody>
        {postForPanel && (
          <Suspense fallback={null}>
            <WallPostViewerModal
              post={postForPanel}
              onClose={close}
              onVote={handlePostVote}
              onCommentAdded={() => queryClient.invalidateQueries({ queryKey: ['/api/wall'] })}
              currentUserId={(currentUser as any)?.id}
              language={(currentUser as any)?.language || 'it'}
              panelMode
            />
          </Suspense>
        )}
      </DetailPanel>
    );
  }

  if (current.kind === 'notifications') {
    return (
      <DetailPanel bareBody>
        <Suspense fallback={null}>
          <NotificationsFeedPanel onUserClick={(uid) => open({ kind: 'profile', id: uid })} />
        </Suspense>
      </DetailPanel>
    );
  }

  if (current.kind === 'chat') {
    const otherUserId = Number(current.id);
    if (!Number.isFinite(otherUserId)) return null;
    // No breadcrumb — the conversation header inside ChatConversationView already
    // shows who you're talking to.
    return (
      <DetailPanel bareBody>
        <Suspense fallback={null}>
          <ChatConversationView
            userId={otherUserId}
            onBack={close}
            onOpenProfile={(uid) => open({ kind: 'profile', id: uid })}
            className="absolute inset-0 flex flex-col"
            context="page"
          />
        </Suspense>
      </DetailPanel>
    );
  }

  if (current.kind === 'group') {
    const groupId = Number(current.id);
    if (!Number.isFinite(groupId)) return null;
    return (
      <DetailPanel crumb={crumb} bareBody>
        <Suspense fallback={null}>
          <GroupDetailSheet
            groupId={groupId}
            onOpenProfile={(uid) => push({ kind: 'profile', id: uid })}
            onOpenEvent={(eid) => push({ kind: 'event', id: eid })}
          />
        </Suspense>
      </DetailPanel>
    );
  }

  return <DetailPanel crumb={crumb} />;
}

/**
 * Edit toggle rendered in the DetailPanel chrome for the user's own profile.
 * Dispatches the existing `toggleProfileEditMode` document event that
 * UserProfileBottomSheet already listens to (same wiring as the mobile Edit
 * button at the top of the profile sheet). Local state resets whenever the
 * panel switches to a different profile id.
 */
function ProfileEditChromeAction({ panelKey }: { panelKey: string | number }) {
  const [isEditing, setIsEditing] = useState(false);
  useEffect(() => {
    setIsEditing(false);
  }, [panelKey]);

  const toggle = () => {
    const next = !isEditing;
    setIsEditing(next);
    document.dispatchEvent(
      new CustomEvent('toggleProfileEditMode', { detail: { isEditMode: next } })
    );
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isEditing ? 'Chiudi modifica' : 'Modifica profilo'}
      title={isEditing ? 'Chiudi' : 'Modifica'}
      style={{
        height: 30,
        padding: '0 12px',
        borderRadius: 8,
        background: isEditing ? 'rgba(236,57,66,0.16)' : 'rgba(255,255,255,0.06)',
        border: `1px solid ${isEditing ? 'rgba(236,57,66,0.45)' : 'rgba(255,255,255,0.14)'}`,
        color: isEditing ? '#ec3942' : 'rgba(255,255,255,0.72)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
        flexShrink: 0,
      }}
    >
      {isEditing ? <X size={14} strokeWidth={2} /> : <Pencil size={14} strokeWidth={2} />}
      {isEditing ? 'Chiudi' : 'Modifica'}
    </button>
  );
}

export default DetailPanelHost;
